import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { MapPin, CheckCircle, ArrowRight, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ISSUES = [
  { key: "housing",   label: "Housing", icon: "🏠", desc: "Affordability, rentals, homelessness" },
  { key: "climate",   label: "Climate", icon: "⚡", desc: "Energy, emissions, renewables" },
  { key: "health",    label: "Health",  icon: "🏥", desc: "Hospitals, mental health, Medicare" },
  { key: "economy",   label: "Economy", icon: "💰", desc: "Cost of living, jobs, tax" },
  { key: "education", label: "Education", icon: "📚", desc: "Schools, TAFE, universities" },
  { key: "transport", label: "Transport", icon: "🚆", desc: "Infrastructure, roads, rail" },
  { key: "integrity", label: "Integrity", icon: "⚖️", desc: "Anti-corruption, transparency" },
  { key: "social",    label: "Social Policy", icon: "🤝", desc: "Rights, welfare, inclusion" },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();

  const [step, setStep] = useState(1);
  const [postcode, setPostcode] = useState("");
  const [postcodeResult, setPostcodeResult] = useState(null);
  const [postcodeError, setPostcodeError] = useState("");
  const [postcodeLoading, setPostcodeLoading] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [saving, setSaving] = useState(false);

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  const checkPostcode = async () => {
    if (!/^\d{4}$/.test(postcode.trim())) {
      setPostcodeError("Enter a valid 4-digit Australian postcode");
      return;
    }
    setPostcodeLoading(true);
    setPostcodeError("");
    try {
      const r = await axios.get(`${API}/electorates/postcode/${postcode.trim()}`);
      if (r.data.found) {
        setPostcodeResult(r.data);
      } else {
        setPostcodeError("Postcode not found. You can still continue and select your electorate manually on your dashboard.");
        setPostcodeResult({ found: false, postcode: postcode.trim() });
      }
    } catch { setPostcodeError("Could not look up postcode. Try again."); }
    setPostcodeLoading(false);
  };

  const toggleIssue = (key) => {
    setSelectedIssues((prev) =>
      prev.includes(key) ? prev.filter((i) => i !== key) : prev.length < 3 ? [...prev, key] : prev
    );
  };

  const finish = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/profile`, {
        postcode: postcode.trim() || undefined,
        my_issues: selectedIssues,
      }, { withCredentials: true });
    } catch {}
    setSaving(false);
    navigate("/dashboard");
  };

  const skip = () => navigate("/dashboard");

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ backgroundColor: bg }} data-testid="onboarding-page">
      {/* Header */}
      <div className="mb-8 text-center">
        <button onClick={() => navigate("/")} className="text-2xl font-black mb-2 block" style={{ color: "#FFC82E", fontFamily: "Outfit, sans-serif" }}>
          AusVote.
        </button>
        <p className="text-sm" style={{ color: textSec }}>Let's personalise your experience</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className="w-7 h-7 border-2 border-black flex items-center justify-center text-xs font-black"
              style={{ backgroundColor: step >= s ? "#FFC82E" : (isDark ? "#1E293B" : "#fff"), color: "#000" }}
            >
              {step > s ? "✓" : s}
            </div>
            {s < 3 && <div className="w-8 h-0.5 border-t-2 border-black border-dashed" />}
          </div>
        ))}
        <span className="ml-2 text-xs font-bold" style={{ color: textSec }}>
          Step {step} of 3
        </span>
      </div>

      <div className="w-full max-w-lg">
        {/* ── Step 1: Postcode ─────────────────────────── */}
        {step === 1 && (
          <div className="border-2 border-black p-6" style={{ backgroundColor: surface, boxShadow: "6px 6px 0 #000" }} data-testid="onboarding-step-1">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-5 h-5" style={{ color: "#FFC82E" }} />
              <h2 className="font-black text-xl" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>Where do you vote?</h2>
            </div>
            <p className="text-sm mb-5" style={{ color: textSec }}>
              Enter your postcode and we'll find your federal electorate and local MP.
            </p>
            <div className="flex gap-2 mb-4">
              <input
                className="flex-1 border-2 border-black px-3 py-2 text-base font-bold"
                style={{ backgroundColor: isDark ? "#0F172A" : "#F9F7F2", color: text }}
                placeholder="e.g. 3000"
                maxLength={4}
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && checkPostcode()}
                data-testid="onboarding-postcode-input"
              />
              <button
                onClick={checkPostcode}
                disabled={postcodeLoading}
                className="px-4 py-2 font-black border-2 border-black hover:-translate-y-0.5 transition-transform"
                style={{ backgroundColor: "#FFC82E", color: "#000", boxShadow: "2px 2px 0 #000" }}
                data-testid="onboarding-postcode-btn"
              >
                {postcodeLoading ? "…" : "Look up"}
              </button>
            </div>

            {postcodeError && <p className="text-xs mb-3" style={{ color: "#EF4444" }}>{postcodeError}</p>}

            {postcodeResult?.found && (
              <div className="border-2 border-black p-3 mb-4" style={{ backgroundColor: isDark ? "#0F172A" : "#FFF9E6" }}>
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4" style={{ color: "#10B981" }} />
                  <p className="font-black text-sm" style={{ color: text }}>Found your electorate!</p>
                </div>
                <p className="text-sm" style={{ color: textSec }}>
                  Federal: <span className="font-black" style={{ color: text }}>{postcodeResult.federal_electorate}</span>
                  {" · "}{postcodeResult.state_code}
                </p>
                {postcodeResult.next_state_election && (
                  <p className="text-xs mt-1" style={{ color: textSec }}>
                    Next state election: <span className="font-black">{postcodeResult.next_state_election.name}</span>
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-2 mt-2">
              <button
                onClick={() => setStep(2)}
                disabled={!postcodeResult}
                className="flex-1 py-2.5 font-black border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-40"
                style={{ backgroundColor: "#1F2937", color: "#fff", boxShadow: "2px 2px 0 #000" }}
                data-testid="onboarding-next-1"
              >
                Next → Pick your issues
              </button>
              <button onClick={() => setStep(2)} className="text-xs font-bold px-3 py-2 border-2 border-black hover:opacity-75" style={{ color: textSec }}>
                Skip
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Issues ───────────────────────────── */}
        {step === 2 && (
          <div className="border-2 border-black p-6" style={{ backgroundColor: surface, boxShadow: "6px 6px 0 #000" }} data-testid="onboarding-step-2">
            <h2 className="font-black text-xl mb-1" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              What matters to you?
            </h2>
            <p className="text-sm mb-5" style={{ color: textSec }}>
              Pick up to 3 issues. We'll filter your news feed and highlight party policies on these topics.
              {selectedIssues.length >= 3 && <span style={{ color: "#FFC82E" }}> (3 selected — max)</span>}
            </p>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {ISSUES.map((issue) => {
                const selected = selectedIssues.includes(issue.key);
                return (
                  <button
                    key={issue.key}
                    onClick={() => toggleIssue(issue.key)}
                    disabled={!selected && selectedIssues.length >= 3}
                    className="flex items-start gap-2 p-3 border-2 border-black text-left hover:-translate-y-0.5 transition-transform disabled:opacity-40"
                    style={{ backgroundColor: selected ? "#1F2937" : (isDark ? "#0F172A" : "#F9F7F2"), boxShadow: selected ? "2px 2px 0 #000" : "none" }}
                    data-testid={`onboarding-issue-${issue.key}`}
                  >
                    <span className="text-xl leading-none">{issue.icon}</span>
                    <div>
                      <p className="font-black text-sm" style={{ color: selected ? "#fff" : text }}>{issue.label}</p>
                      <p className="text-xs" style={{ color: selected ? "#94A3B8" : textSec }}>{issue.desc}</p>
                    </div>
                    {selected && <CheckCircle className="w-4 h-4 ml-auto flex-shrink-0 text-yellow-400" />}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep(1)} className="text-xs font-bold px-3 py-2 border-2 border-black hover:opacity-75" style={{ color: textSec }}>
                ← Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="flex-1 py-2.5 font-black border-2 border-black hover:-translate-y-0.5 transition-transform"
                style={{ backgroundColor: "#1F2937", color: "#fff", boxShadow: "2px 2px 0 #000" }}
                data-testid="onboarding-next-2"
              >
                Next → See your dashboard
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Confirm ─────────────────────────── */}
        {step === 3 && (
          <div className="border-2 border-black p-6" style={{ backgroundColor: surface, boxShadow: "6px 6px 0 #000" }} data-testid="onboarding-step-3">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5" style={{ color: "#FFC82E" }} />
              <h2 className="font-black text-xl" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>You're set up!</h2>
            </div>
            <div className="space-y-3 mb-6">
              {postcodeResult?.found && (
                <div className="flex items-center gap-3 border-2 border-black p-3" style={{ backgroundColor: isDark ? "#0F172A" : "#F0FDF4" }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: "#10B981" }} />
                  <p className="text-sm" style={{ color: text }}>
                    Electorate: <strong>{postcodeResult.federal_electorate}</strong>, {postcodeResult.state_code}
                  </p>
                </div>
              )}
              {selectedIssues.length > 0 && (
                <div className="flex items-start gap-3 border-2 border-black p-3" style={{ backgroundColor: isDark ? "#0F172A" : "#F0FDF4" }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#10B981" }} />
                  <p className="text-sm" style={{ color: text }}>
                    Issues: <strong>{selectedIssues.join(", ")}</strong>
                  </p>
                </div>
              )}
              <div className="border-2 border-black p-3" style={{ backgroundColor: isDark ? "#0F172A" : "#FFF9E6" }}>
                <p className="text-xs font-bold" style={{ color: textSec }}>
                  Your dashboard will now show your local MP, personalised news, election checklist, and let you follow politicians to track their promises.
                </p>
              </div>
            </div>
            <button
              onClick={finish}
              disabled={saving}
              className="w-full py-3 font-black border-2 border-black hover:-translate-y-0.5 transition-transform flex items-center justify-center gap-2"
              style={{ backgroundColor: "#FFC82E", color: "#000", boxShadow: "3px 3px 0 #000" }}
              data-testid="onboarding-finish-btn"
            >
              {saving ? "Saving…" : "Go to My Dashboard"} <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={skip} className="mt-2 w-full text-xs font-bold py-2 hover:opacity-75" style={{ color: textSec }}>
              Skip for now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
